import * as yup from 'yup'

import { onlyNumber } from 'utils'

const isCNPJ = (document?: string | null) =>
  onlyNumber(document || '').length === 14

// SC-10941: com o cartão na Stone, o recebedor exige renda e ocupação (da pessoa
// física, ou do sócio na PJ) e o telefone do sócio. Só para quem está na Stone.
const stoneIndividual = (isStone?: boolean, document?: string | null) =>
  !!isStone && !isCNPJ(document)
const stoneCompany = (isStone?: boolean, document?: string | null) =>
  !!isStone && isCNPJ(document)

const requiredWhen = (
  check: (isStone?: boolean, document?: string | null) => boolean,
  message: string
) =>
  yup
    .string()
    .nullable(true)
    .when(['isStone', 'document'], {
      is: check,
      then: yup.string().nullable(true).required(message),
      otherwise: yup.string().nullable(true).notRequired()
    })

const moneyRequiredWhen = (
  check: (isStone?: boolean, document?: string | null) => boolean,
  message: string
) =>
  requiredWhen(check, message).test(
    'positive-money',
    message,
    function (value) {
      const { isStone, document } = this.parent
      if (!check(isStone, document)) return true
      return Number(onlyNumber(value || '')) > 0
    }
  )

export const schemaInformationForm = yup.object({
  document: yup.string().required('CPF/CNPJ é obrigatório.').nullable(true),
  isCPF: yup.boolean(),
  legal_name: yup.string().required('Razão social obrigatória').nullable(true),
  owner_document: yup
    .string()
    .nullable(true)
    .when('document', {
      is: isCNPJ,
      then: yup.string().nullable(true).required('CPF do sócio obrigatório'),
      otherwise: yup.string().nullable(true).notRequired()
    }),
  owner_name: yup
    .string()
    .nullable(true)
    .when('document', {
      is: isCNPJ,
      then: yup.string().nullable(true).required('Nome do sócio obrigatório'),
      otherwise: yup.string().nullable(true).notRequired()
    }),
  owner_birthdate: yup
    .date()
    .nullable()
    .when('document', {
      is: isCNPJ,
      then: yup
        .date()
        .nullable()
        .required('Data de nascimento do sócio obrigatória.'),
      otherwise: yup.date().nullable().notRequired()
    }),
  birthdate: yup.date().nullable().required('Data obrigatório.'),
  revenue: yup
    .string()
    .max(10, 'Valor deve ser menor que R$100.000.000,00')
    .typeError('Faturamento obrigatório.')
    .required('Faturamento obrigatório.'),
  isStone: yup.boolean(),
  monthly_income: moneyRequiredWhen(
    stoneIndividual,
    'Renda mensal obrigatória.'
  ),
  professional_occupation: requiredWhen(
    stoneIndividual,
    'Ocupação profissional obrigatória.'
  ),
  owner_phone: requiredWhen(
    stoneCompany,
    'Telefone do sócio obrigatório.'
  ).test('phone-format', 'Digite um telefone válido.', (value) => {
    if (!value) return true
    const digits = value.replace(/\D/g, '')
    return digits.length >= 10 && digits.length <= 11
  }),
  owner_monthly_income: moneyRequiredWhen(
    stoneCompany,
    'Renda mensal do sócio obrigatória.'
  ),
  owner_professional_occupation: requiredWhen(
    stoneCompany,
    'Ocupação profissional do sócio obrigatória.'
  )
})

export type InformationFormSchema = yup.InferType<typeof schemaInformationForm>
